import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    image: '',
    headline: '',
    highlight_word: '',
    subheading: '',
    cta: 'View Our Space',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await apiClient.get('/hero-slides');
      setSlides(response.data);
    } catch (error) {
      console.error('Error fetching hero slides:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        highlight_word: formData.highlight_word || null,
        order: parseInt(formData.order) || 0,
        id: editingSlide?.id || undefined
      };

      if (editingSlide) {
        await apiClient.put(`/admin/hero-slides/${editingSlide.id}`, payload);
        toast.success('Hero slide updated successfully!');
      } else {
        await apiClient.post('/admin/hero-slides', payload);
        toast.success('Hero slide created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchSlides();
    } catch (error) {
      toast.error('Failed to save hero slide');
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      image: slide.image,
      headline: slide.headline,
      highlight_word: slide.highlight_word || '',
      subheading: slide.subheading,
      cta: slide.cta,
      order: slide.order,
      is_active: slide.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await apiClient.delete(`/admin/hero-slides/${id}`);
        toast.success('Hero slide deleted successfully!');
        fetchSlides();
      } catch (error) {
        toast.error('Failed to delete hero slide');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      image: '',
      headline: '',
      highlight_word: '',
      subheading: '',
      cta: 'View Our Space',
      order: 0,
      is_active: true
    });
    setEditingSlide(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Hero Slider Management</h1>
              <p className="text-gray-600 mt-1">Manage homepage hero slider backgrounds and content</p>
            </div>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Slide
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-4">
          {slides.map((slide) => (
            <div key={slide.id} className="bg-white rounded-lg shadow-md overflow-hidden flex">
              <img src={slide.image} alt={slide.headline} className="w-64 h-40 object-cover" />
              <div className="p-4 flex-1">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-semibold bg-gray-200 px-2 py-1 rounded">
                        Order: {slide.order}
                      </span>
                      {slide.is_active ? (
                        <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-1 rounded">
                          Active
                        </span>
                      ) : (
                        <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{slide.headline}</h3>
                    <p className="text-gray-600 text-sm mb-2">{slide.subheading}</p>
                    <p className="text-gray-500 text-xs">CTA: {slide.cta}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={() => handleEdit(slide)} size="sm" variant="outline">
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button onClick={() => handleDelete(slide.id)} size="sm" variant="destructive">
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSlide ? 'Edit Hero Slide' : 'Add New Hero Slide'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUpload
              label="Background Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
              required={true}
              preview={true}
            />
            <div>
              <Label>Headline</Label>
              <Input
                value={formData.headline}
                onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                placeholder="Where Ambitions Take Shape"
                required
              />
            </div>
            <div>
              <Label>Highlight Word (Optional)</Label>
              <Input
                value={formData.highlight_word}
                onChange={(e) => setFormData({ ...formData, highlight_word: e.target.value })}
                placeholder="Ambitions"
              />
              <p className="text-sm text-gray-500 mt-1">Word from headline to highlight with red background</p>
            </div>
            <div>
              <Label>Subheading</Label>
              <Textarea
                value={formData.subheading}
                onChange={(e) => setFormData({ ...formData, subheading: e.target.value })}
                placeholder="A workspace where vision meets precision..."
                required
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CTA Button Text</Label>
                <Input
                  value={formData.cta}
                  onChange={(e) => setFormData({ ...formData, cta: e.target.value })}
                  placeholder="View Our Space"
                  required
                />
              </div>
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  placeholder="0"
                  required
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active (Show on website)</Label>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Slide
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
